// src/pages/SanChoi.js
import React, { useState, useEffect, useCallback } from "react";
import { fetchData, addData, updateData, deleteData, guiAnh } from "../services/api";
import { useThongBao, ThongBao, DangTai, hoiXoa } from "../vo";
import AnhCoKhoa from "../components/AnhCoKhoa";
import XemAnh from "../components/XemAnh";
import { thuNhoAnh } from "../utils/thuNhoAnh";
import { tachLink, laLinkHopLe } from "../utils/meoVat";
import { CAM_XUC, ANH_TOI_DA, gomCamXuc, camXucCuaToi, veLucDang } from "../utils/sanChoi";

// ============================================================
// 🎉 SÂN CHƠI (Đợt N7, 15/09/2026)
// ------------------------------------------------------------
// Chân dung 4.7 — vế "thích" của chiến lược: nhắc việc thì không ai thích,
// sân chơi mới là thứ khiến cả nhà tự mở app.
//
// Anh chốt 15/09 (chân dung mục 10), mockup `mockup/N7_san_choi.html`:
//   SC1 nút trên thanh trên cùng + chấm đỏ có bài mới (App.js, ThanhDieuHuong)
//   SC2 tối đa 6 ảnh mỗi bài · SC3 5 cảm xúc, hiện TÊN không đếm
//   SC4 bài mới không lên Bảng tin · SC5 ảnh giữ lâu dài (máy chủ lo)
//
// ⚠️ KHÔNG BÌNH LUẬN (mục 7). ⚠️ KHÔNG CON SỐ (5.4) — kể cả "5 người thích".
//
// ⚠️ Component con nằm NGOÀI hàm SanChoi — bài học 06 (ô nhập mất con trỏ).
// ============================================================

// ----- Chữ của bài: giữ xuống dòng, link bấm được -----
function ChuBai({ chu }) {
  if (!chu) return null;
  return (
    <div className="sc-chu">
      {chu.split("\n").map((dong, i) => (
        <div key={i} className={dong.trim() === "" ? "mv-dong-trong" : undefined}>
          {tachLink(dong).map((m, j) =>
            m.link ? (
              <a key={j} href={m.link} target="_blank" rel="noopener noreferrer" className="mv-link">
                {m.link}
              </a>
            ) : (
              <React.Fragment key={j}>{m.chu}</React.Fragment>
            ),
          )}
        </div>
      ))}
    </div>
  );
}

// ----- Lưới ảnh: 1 ảnh to · 2–6 ảnh lưới 2 cột, quá 4 thì ô cuối "+N" -----
function LuoiAnh({ dsAnh, onMo }) {
  const con = dsAnh.filter((a) => !a.da_don);
  if (con.length === 0) return null;
  if (con.length === 1) {
    return (
      <button className="sc-anh-mot" onClick={() => onMo(0)} aria-label="Xem ảnh to">
        <AnhCoKhoa anhId={con[0].id} moTa="Ảnh" className="sc-anh-hinh" />
      </button>
    );
  }
  const hien = con.slice(0, 4);
  return (
    <div className="sc-luoi">
      {hien.map((a, i) => (
        <button key={a.id} className="sc-o-anh" onClick={() => onMo(i)} aria-label={`Xem ảnh ${i + 1}`}>
          <AnhCoKhoa anhId={a.id} moTa={`Ảnh ${i + 1}`} className="sc-anh-hinh" />
          {i === 3 && con.length > 4 && <span className="sc-them-anh">+{con.length - 4}</span>}
        </button>
      ))}
    </div>
  );
}

// ----- Hàng 5 cảm xúc + tên người thả -----
function HangCamXuc({ bai, toiId, dangBam, onBam }) {
  const cuaToi = camXucCuaToi(bai.cam_xuc, toiId);
  const nhom = gomCamXuc(bai.cam_xuc, toiId);
  return (
    <>
      <div className="sc-hang-cx" role="group" aria-label="Thả cảm xúc">
        {CAM_XUC.map((c) => (
          <button
            key={c.ma}
            className={`sc-cx ${cuaToi === c.ma ? "sc-cx-bat" : ""}`}
            onClick={() => onBam(c.ma)}
            disabled={dangBam}
            title={cuaToi === c.ma ? "Bấm lần nữa để bỏ" : c.ten}
            aria-pressed={cuaToi === c.ma}
          >
            {c.emoji}
          </button>
        ))}
      </div>
      {nhom.length > 0 && (
        <div className="sc-ten-cx">
          {nhom.map((n, i) => (
            <span key={n.emoji}>
              {i > 0 && " · "}
              <strong>{n.ten}</strong> {n.emoji}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

// ----- Chọn ảnh + xem trước (dùng cho Đăng và Sửa) -----
function ChonAnh({ tepMoi, soAnhCu, onThem, onBo, dangGui }) {
  const [xemTruoc, setXemTruoc] = useState([]);
  useEffect(() => {
    const urls = tepMoi.map((t) => URL.createObjectURL(t));
    setXemTruoc(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [tepMoi]);

  const conCho = ANH_TOI_DA - soAnhCu - tepMoi.length;
  return (
    <>
      {xemTruoc.length > 0 && (
        <div className="sc-anh-chon">
          {xemTruoc.map((u, i) => (
            <span key={u} className="sc-o-chon">
              <img src={u} alt={`Ảnh chọn ${i + 1}`} />
              <button type="button" onClick={() => onBo(i)} aria-label="Bỏ ảnh này" disabled={Boolean(dangGui)}>
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      {conCho > 0 ? (
        /* Không kèm `capture`: điện thoại hỏi "Chụp ảnh hay Chọn từ thư viện" (N5).
           `multiple`: chọn một lượt nhiều tấm (SC2). */
        <label className={`ct-nut-gan-anh ${dangGui ? "ct-nut-tat" : ""}`}>
          📷 {tepMoi.length + soAnhCu > 0 ? "Thêm ảnh" : "Ảnh"}
          <input
            type="file"
            accept="image/*"
            multiple
            disabled={Boolean(dangGui)}
            onChange={(e) => {
              const ds = Array.from(e.target.files || []);
              e.target.value = "";
              if (ds.length) onThem(ds);
            }}
          />
        </label>
      ) : (
        <span className="sc-du-anh">Đủ {ANH_TOI_DA} ảnh</span>
      )}
    </>
  );
}

// ----- Một bài -----
function Bai({ bai, user, laAdmin, dangSua, formSua, onMoSua, onDoiFormSua, onLuuSua, onHuySua, onXoa, onBamCamXuc, dangBamCx, onMoAnh, dangGui }) {
  const suaDuoc = laAdmin || bai.user_id === user.id;

  if (dangSua) {
    const anhCon = bai.anh.filter((a) => !a.da_don && !formSua.boAnh.includes(a.id));
    return (
      <li className="card sc-bai">
        <strong>✏️ Sửa bài</strong>
        <textarea
          className="dx-o sc-o-viet"
          value={formSua.noi_dung}
          onChange={(e) => onDoiFormSua({ noi_dung: e.target.value })}
          maxLength={2000}
          rows={3}
        />
        {anhCon.length > 0 && (
          <div className="sc-anh-chon">
            {anhCon.map((a) => (
              <span key={a.id} className="sc-o-chon">
                <AnhCoKhoa anhId={a.id} moTa="Ảnh" />
                <button type="button" onClick={() => onDoiFormSua({ boAnh: [...formSua.boAnh, a.id] })} aria-label="Bỏ ảnh này">
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
        <ChonAnh
          tepMoi={formSua.tepMoi}
          soAnhCu={anhCon.length}
          onThem={(ds) => onDoiFormSua({ tepMoi: [...formSua.tepMoi, ...ds].slice(0, ANH_TOI_DA - anhCon.length) })}
          onBo={(i) => onDoiFormSua({ tepMoi: formSua.tepMoi.filter((_, j) => j !== i) })}
          dangGui={dangGui}
        />
        <input
          type="url"
          className="mv-o sc-o-link"
          value={formSua.link}
          onChange={(e) => onDoiFormSua({ link: e.target.value })}
          placeholder="🔗 Link video (không bắt buộc)"
          maxLength={500}
        />
        <div className="mv-hang-nut">
          <button className="nut nut-xanh mv-nut-to" onClick={onLuuSua} disabled={Boolean(dangGui)}>
            {dangGui || "Lưu thay đổi"}
          </button>
          <button className="nut nut-xam mv-nut-to" onClick={onHuySua} disabled={Boolean(dangGui)}>
            Thôi
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="card sc-bai">
      <div className="sc-dau-bai">
        <strong className="sc-ten">{bai.ten_nguoi_dang || "Ai đó"}</strong>
        <span className="sc-luc">
          {veLucDang(bai)}
          {bai.da_sua && " · đã sửa"}
        </span>
        {suaDuoc && (
          <span className="db-nut">
            <button onClick={onMoSua} className="btn-edit" title="Sửa">✏️</button>
            <button onClick={onXoa} className="btn-delete" title="Xoá">🗑️</button>
          </span>
        )}
      </div>
      <ChuBai chu={bai.noi_dung} />
      <LuoiAnh dsAnh={bai.anh} onMo={onMoAnh} />
      {bai.link && (
        <a href={bai.link} target="_blank" rel="noopener noreferrer" className="mv-link mv-link-rieng">
          🔗 {bai.link}
        </a>
      )}
      <HangCamXuc bai={bai} toiId={user.id} dangBam={dangBamCx} onBam={onBamCamXuc} />
    </li>
  );
}

const FORM_DANG = { noi_dung: "", link: "", tepMoi: [], moLink: false };

function SanChoi({ user, onDaXem }) {
  const laAdmin = user?.role === "admin";
  const [ds, setDs] = useState([]);
  const [conNua, setConNua] = useState(false);
  const [dangTai, setDangTai] = useState(true);
  const [loiTai, setLoiTai] = useState(false);

  const [form, setForm] = useState(FORM_DANG);
  const [dangGui, setDangGui] = useState("");

  const [suaId, setSuaId] = useState(null);
  const [formSua, setFormSua] = useState(null);
  const [dangBamCx, setDangBamCx] = useState(null);
  const [xem, setXem] = useState(null); // { bai, viTri }

  const { bao, noiBao } = useThongBao(6000);

  const taiLai = useCallback(async () => {
    try {
      const d = await fetchData("san-choi");
      setDs(Array.isArray(d.bai) ? d.bai : []);
      setConNua(Boolean(d.con_nua));
      setLoiTai(false);
    } catch (err) {
      setLoiTai(true);
    } finally {
      setDangTai(false);
    }
  }, []);

  // Mở trang = đã xem → chấm đỏ trên nút tắt (SC1).
  useEffect(() => {
    taiLai();
    addData("san-choi/da-xem", {})
      .then(() => onDaXem && onDaXem())
      .catch(() => {});
  }, [taiLai, onDaXem]);

  const taiThem = async () => {
    const cuoi = ds[ds.length - 1];
    if (!cuoi) return;
    try {
      const d = await fetchData(`san-choi?truoc=${cuoi.id}`);
      setDs((cu) => [...cu, ...d.bai]);
      setConNua(Boolean(d.con_nua));
    } catch (err) {
      noiBao("⚠️ Chưa tải được bài cũ hơn");
    }
  };

  // Gửi lần lượt từng tấm — rớt mạng giữa chừng thì những tấm đã lên vẫn còn.
  const guiCacAnh = async (baiId, tepMoi) => {
    let hong = 0;
    for (let i = 0; i < tepMoi.length; i++) {
      setDangGui(`Đang gửi ảnh ${i + 1}/${tepMoi.length}…`);
      try {
        const blob = await thuNhoAnh(tepMoi[i]);
        await guiAnh(`san-choi/${baiId}/anh`, blob);
      } catch (err) {
        hong += 1;
      }
    }
    return hong;
  };

  const dang = async () => {
    const noiDung = form.noi_dung.trim();
    const link = form.link.trim();
    if (!noiDung && !link && form.tepMoi.length === 0) {
      return noiBao("⚠️ Viết vài chữ, chọn ảnh hoặc dán link rồi mới đăng nhé");
    }
    if (link && !laLinkHopLe(link)) return noiBao("⚠️ Link phải bắt đầu bằng http:// hoặc https://");

    setDangGui("Đang đăng…");
    let baiId;
    try {
      baiId = (await addData("san-choi", { noi_dung: noiDung, link })).id;
    } catch (err) {
      setDangGui("");
      return noiBao(`⚠️ Chưa đăng được${err.chiTiet ? ` — ${err.chiTiet}` : ""}`);
    }
    const hong = await guiCacAnh(baiId, form.tepMoi);
    // Bài chỉ có ảnh mà không tấm nào lên → bài trống, xoá đi cho khỏi rác.
    if (hong > 0 && hong === form.tepMoi.length && !noiDung && !link) {
      await deleteData("san-choi", baiId).catch(() => {});
      setDangGui("");
      return noiBao("⚠️ Không gửi được ảnh nào — bài chưa đăng. Thử lại giúp nhé.", 9000);
    }
    setDangGui("");
    setForm(FORM_DANG);
    await taiLai();
    noiBao(hong > 0 ? `⚠️ Đã đăng, nhưng ${hong} ảnh chưa lên — bấm ✏️ để thêm lại` : "✅ Đã đăng lên Sân chơi");
  };

  const moSua = (b) => {
    setSuaId(b.id);
    setFormSua({ noi_dung: b.noi_dung || "", link: b.link || "", tepMoi: [], boAnh: [] });
  };

  const luuSua = async (b) => {
    const link = formSua.link.trim();
    if (link && !laLinkHopLe(link)) return noiBao("⚠️ Link phải bắt đầu bằng http:// hoặc https://");
    const conAnh = b.anh.filter((a) => !a.da_don && !formSua.boAnh.includes(a.id)).length + formSua.tepMoi.length;
    if (!formSua.noi_dung.trim() && !link && conAnh === 0) {
      return noiBao("⚠️ Bài không còn gì cả — muốn bỏ bài thì bấm 🗑️");
    }
    setDangGui("Đang lưu…");
    try {
      await updateData("san-choi", b.id, { noi_dung: formSua.noi_dung, link });
      for (const anhId of formSua.boAnh) await deleteData(`san-choi/${b.id}/anh`, anhId);
    } catch (err) {
      setDangGui("");
      return noiBao(`⚠️ Chưa lưu được${err.chiTiet ? ` — ${err.chiTiet}` : ""}`);
    }
    const hong = await guiCacAnh(b.id, formSua.tepMoi);
    setDangGui("");
    setSuaId(null);
    await taiLai();
    noiBao(hong > 0 ? `⚠️ Đã lưu, nhưng ${hong} ảnh chưa lên` : "✅ Đã lưu thay đổi");
  };

  const xoa = async (b) => {
    const ten = (b.noi_dung || "bài có ảnh").slice(0, 40);
    if (!hoiXoa("bài", ten)) return;
    try {
      await deleteData("san-choi", b.id);
      await taiLai();
    } catch (err) {
      noiBao(`⚠️ Chưa xoá được${err.chiTiet ? ` — ${err.chiTiet}` : ""}`);
    }
  };

  // Bấm cảm xúc: đang là cái đó → bỏ; khác → đổi. Vẽ ngay tại chỗ rồi mới gửi.
  const bamCamXuc = async (b, ma) => {
    const cuaToi = camXucCuaToi(b.cam_xuc, user.id);
    const khac = (b.cam_xuc || []).filter((c) => c.user_id !== user.id);
    const moi = cuaToi === ma ? khac : [...khac, { user_id: user.id, ten: user.full_name, loai: ma }];
    setDs((cu) => cu.map((x) => (x.id === b.id ? { ...x, cam_xuc: moi } : x)));
    setDangBamCx(b.id);
    try {
      if (cuaToi === ma) await deleteData(`san-choi/${b.id}`, "cam-xuc");
      else await updateData(`san-choi/${b.id}`, "cam-xuc", { loai: ma });
    } catch (err) {
      noiBao("⚠️ Chưa ghi nhận được, thử lại giúp nhé");
      taiLai();
    } finally {
      setDangBamCx(null);
    }
  };

  if (dangTai) return <DangTai gi="Sân chơi" />;

  const anhXem = xem ? xem.bai.anh.filter((a) => !a.da_don) : [];

  return (
    <>
      <div className="card">
        <h3>🎉 Sân chơi</h3>
        <ThongBao bao={bao} />
        <textarea
          className="dx-o sc-o-viet"
          value={form.noi_dung}
          onChange={(e) => setForm((f) => ({ ...f, noi_dung: e.target.value }))}
          placeholder="😊 Hôm nay nhà mình có gì vui?"
          maxLength={2000}
          rows={3}
          disabled={Boolean(dangGui)}
        />
        <ChonAnh
          tepMoi={form.tepMoi}
          soAnhCu={0}
          onThem={(dsTep) => {
            if (form.tepMoi.length + dsTep.length > ANH_TOI_DA) noiBao(`Mỗi bài tối đa ${ANH_TOI_DA} ảnh — đã lấy ${ANH_TOI_DA} tấm đầu`);
            setForm((f) => ({ ...f, tepMoi: [...f.tepMoi, ...dsTep].slice(0, ANH_TOI_DA) }));
          }}
          onBo={(i) => setForm((f) => ({ ...f, tepMoi: f.tepMoi.filter((_, j) => j !== i) }))}
          dangGui={dangGui}
        />
        {form.moLink ? (
          <input
            type="url"
            className="mv-o sc-o-link"
            value={form.link}
            onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
            placeholder="🔗 Dán link video (YouTube, TikTok…)"
            maxLength={500}
            autoFocus
          />
        ) : (
          <button type="button" className="cho-nut-chu" onClick={() => setForm((f) => ({ ...f, moLink: true }))}>
            🔗 + thêm link video
          </button>
        )}
        <div className="sc-hang-dang">
          {form.tepMoi.length > 0 && <span className="sc-dem-anh">{form.tepMoi.length}/{ANH_TOI_DA} ảnh</span>}
          <button className="nut nut-xanh mv-nut-to" onClick={dang} disabled={Boolean(dangGui)}>
            {dangGui || "Đăng"}
          </button>
        </div>
      </div>

      {loiTai && <p className="reminder-loi">⚠️ Không tải được Sân chơi. Kéo xuống để tải lại.</p>}

      {!loiTai && ds.length === 0 && (
        <p className="db-trong sc-trong">
          Sân chơi còn trống. Đăng bài đầu tiên — một tấm ảnh bữa cơm, một chiến tích, một câu chuyện vui.
        </p>
      )}

      <ul className="sc-ds">
        {ds.map((b) => (
          <Bai
            key={b.id}
            bai={b}
            user={user}
            laAdmin={laAdmin}
            dangSua={suaId === b.id}
            formSua={formSua}
            onMoSua={() => moSua(b)}
            onDoiFormSua={(doi) => setFormSua((f) => ({ ...f, ...doi }))}
            onLuuSua={() => luuSua(b)}
            onHuySua={() => setSuaId(null)}
            onXoa={() => xoa(b)}
            onBamCamXuc={(ma) => bamCamXuc(b, ma)}
            dangBamCx={dangBamCx === b.id}
            onMoAnh={(viTri) => setXem({ bai: b, viTri })}
            dangGui={suaId === b.id ? dangGui : ""}
          />
        ))}
      </ul>

      {conNua && (
        <button className="nut nut-xam mv-nut-to sc-tai-them" onClick={taiThem}>
          Xem bài cũ hơn
        </button>
      )}

      {xem && anhXem[xem.viTri] && (
        <XemAnh
          anhId={anhXem[xem.viTri].id}
          tieuDe={xem.bai.ten_nguoi_dang || "Sân chơi"}
          bieuTuong="🎉"
          thuTu={anhXem.length > 1 ? `${xem.viTri + 1}/${anhXem.length}` : undefined}
          onTruoc={xem.viTri > 0 ? () => setXem((x) => ({ ...x, viTri: x.viTri - 1 })) : undefined}
          onSau={
            anhXem.length > 1 && xem.viTri < anhXem.length - 1
              ? () => setXem((x) => ({ ...x, viTri: x.viTri + 1 }))
              : undefined
          }
          onDong={() => setXem(null)}
        />
      )}
    </>
  );
}

export default SanChoi;
